(() => {
  const configElement = document.getElementById('TTDisplayCurrencyConfig');
  if (!configElement) return;

  const config = JSON.parse(configElement.textContent);
  const day = 86400000;
  const preferenceKey = 'tt-display-country-v1';
  const currency = config.currencies[config.country] || 'JPY';
  const rateKey = `tt-fx-JPY-${currency}-v2`;
  const geoKey = 'tt-display-geo-v1';
  const autoParameter = 'tt_country_auto';
  const availableCountries = new Set(config.countries);
  const formatter = new Intl.NumberFormat(config.locale || 'en', { style: 'currency', currency });
  let rate = null;
  let message = '';
  let manualSelection = false;
  let scheduled = false;
  window.TTDisplayCurrency = { currency: 'JPY', rate: 1, ready: currency === 'JPY' };

  function readCache(key, maxAge) {
    try {
      const item = JSON.parse(localStorage.getItem(key));
      const age = Date.now() - item?.savedAt;
      return item && age >= 0 && age < maxAge ? item.value : null;
    } catch {
      return null;
    }
  }

  function writeCache(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), value }));
      return true;
    } catch { return false; }
  }

  async function fetchJSON(url, external = false) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetch(url, {
        signal: controller.signal,
        credentials: external ? 'omit' : 'same-origin',
        referrerPolicy: external ? 'no-referrer' : 'strict-origin-when-cross-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } finally {
      clearTimeout(timer);
    }
  }

  function validRate(value) {
    const age = Date.now() - Date.parse(value?.date);
    return value?.base === 'JPY' && value?.quote === currency &&
      Number.isFinite(value.rate) && value.rate > 0 &&
      Number.isFinite(age) && age >= -day && age < 7 * day;
  }

  function render() {
    scheduled = false;
    if (rate) {
      document.querySelectorAll('[data-tt-money]').forEach(element => {
        if (element.dataset.ttSourceCurrency !== 'JPY') return;
        const amount = Number(element.dataset.ttMoney);
        const template = element.dataset.ttMoneyTemplate;
        if (!Number.isFinite(amount) || !template?.includes('[money]')) return;
        let text = template.replaceAll('[money]', formatter.format(amount / 100 * rate));
        if (text.includes('[max]')) {
          const maximum = Number(element.dataset.ttMoneyMax);
          if (!Number.isFinite(maximum)) return;
          text = text.replaceAll('[max]', formatter.format(maximum / 100 * rate));
        }
        if (element.textContent !== text) {
          if (!element.title) element.title = element.textContent.trim();
          element.textContent = text;
        }
      });
    }
    document.querySelectorAll('[data-tt-currency-note]').forEach(element => {
      element.hidden = !message;
      const text = [message, config.tax_notice].filter(Boolean).join(' ');
      if (element.textContent !== text) element.textContent = text;
    });
    document.querySelectorAll('[data-tt-current-currency]').forEach(element => {
      const code = rate ? currency : 'JPY';
      if (element.textContent !== code) element.textContent = code;
    });
  }

  function scheduleRender() {
    if (scheduled || (!rate && !message)) return;
    scheduled = true;
    requestAnimationFrame(render);
  }

  async function enableCurrency() {
    message = config.tax_exempt ? config.exempt : config.loading;
    render();
    try {
      let value = readCache(rateKey, day);
      if (!validRate(value)) {
        value = await fetchJSON(`https://api.frankfurter.dev/v2/rate/JPY/${currency}`, true);
        if (!validRate(value)) throw new Error('Invalid or outdated reference rate');
        if (!writeCache(rateKey, value)) throw new Error('Reference rate storage unavailable');
      }
      rate = value.rate;
      message = (config.tax_exempt ? config.exempt_notice : config.notice).replaceAll('[currency]', currency);
    } catch (error) {
      message = config.tax_exempt ? config.exempt : config.unavailable;
      console.warn('[TIKITAKA currency] Reference rate unavailable; displaying JPY.', error);
    }
    render();
    window.TTDisplayCurrency = { currency: rate ? currency : 'JPY', rate: rate || 1, ready: true };
    document.dispatchEvent(new CustomEvent('tt:currency'));
  }

  function preserveURL(form, url = new URL(window.location.href)) {
    let returnTo = form.querySelector('[name="return_to"]');
    if (!returnTo) {
      returnTo = document.createElement('input');
      returnTo.type = 'hidden';
      returnTo.name = 'return_to';
      form.append(returnTo);
    }
    returnTo.value = `${url.pathname}${url.search}${url.hash}`;
  }

  document.addEventListener('submit', event => {
    const button = event.submitter;
    if (!button?.closest('.tt-locale__form')) return;
    preserveURL(button.form);
    if (button.name !== 'country_code' || !availableCountries.has(button.value)) return;
    manualSelection = true;
    writeCache(preferenceKey, button.value);
  }, true);

  window.addEventListener('pageshow', event => {
    if (event.persisted) window.location.reload();
  });

  new MutationObserver(scheduleRender).observe(document.body, { childList: true, subtree: true });

  function selectCountry(country, currentURL) {
    if (!availableCountries.has(country) || country === config.country || manualSelection) return;
    const form = document.getElementById('TTDisplayCurrencyCountry');
    if (!form) return;
    form.querySelector('[name="country_code"]').value = country;
    currentURL.searchParams.set(autoParameter, country);
    preserveURL(form, currentURL);
    form.requestSubmit();
  }

  async function start() {
    if (currency !== 'JPY') enableCurrency();
    const currentURL = new URL(window.location.href);
    const autoAttempt = currentURL.searchParams.get(autoParameter);
    if (autoAttempt || currentURL.searchParams.has('tt_gbp_auto')) {
      currentURL.searchParams.delete(autoParameter);
      currentURL.searchParams.delete('tt_gbp_auto');
      window.history.replaceState(window.history.state, '', currentURL);
      writeCache('tt-country-attempt-v1', autoAttempt || config.country);
      return;
    }
    const preference = readCache(preferenceKey, 30 * day);
    if (availableCountries.has(preference)) {
      if (readCache('tt-country-attempt-v1', 15 * 60000) !== preference) selectCountry(preference, currentURL);
      return;
    }
    try {
      let country = readCache(geoKey, 15 * 60000);
      if (!country) {
        const root = config.root.endsWith('/') ? config.root : `${config.root}/`;
        const result = await fetchJSON(`${root}browsing_context_suggestions.json?country[enabled]=true`);
        country = result.detected_values?.country?.handle;
        if (typeof country === 'string' && /^[A-Z]{2}$/.test(country)) writeCache(geoKey, country);
      }
      if (readCache('tt-country-attempt-v1', 15 * 60000) !== country) selectCountry(country, currentURL);
    } catch (error) {
      console.warn('[TIKITAKA currency] Country detection unavailable; keeping the selected region.', error);
    }
  }

  start();
})();
